import * as React from 'react';
import axios from 'axios';
import moment from 'moment';
import queryString from 'query-string';

import errorReporter from '@app/errorReporter';

import { ListingAvailability, ListingStatus } from '@bb/domain/dist/types';
import { ListingLabelContext, ListingLabelContextProvider } from 'contexts';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Listing, ListingComponent } from './ListingComponent';
import Loading from '../react-common/Loading';

moment.locale('en');

interface SearchParamsType {
  searchType?: string;
  minPrice?: string;
  maxPrice?: string;
  status?: string;
  location?: string;
  locationAddress?: string;
  avail?: string;
  agent?: string;
  type?: string;
  address?: string;
  sort_field?: string;
  sort_asc?: string;
  onboardingPortalId?: string;
}

interface BrokeragePDFProps {
  $window: any;
}

interface BrokeragePDFState {
  creator?: string;
  listings: Listing[];
  loading: boolean;
  searchParams: SearchParamsType;
  agentName?: string;
  branchName?: string;
  subBrokerageName?: string;
}

const InnerBrokeragePDFComponent: React.FC<BrokeragePDFProps> = props => {
  const [state, setState] = useState<BrokeragePDFState>({
    listings: [],
    loading: true,
    searchParams: {},
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const {
          $window: {
            location: { search },
          },
        } = props;
        const searchParams = queryString.parse(search);
        const result = await axios.get(`/my_brokerage_tab_data${search}`);
        const { listings, creator, agentName, branchName, subBrokerageName } =
          result.data;

        const parsedListings = listings.map((l: any) => {
          const namedAgents = l.fullReps.map((agent: any) => `${agent.firstName || ''} ${agent.lastName || ''}`);
          const parsed = {
            id: l.id,
            agents: namedAgents,
            line1: l.line1,
            line2: l.line2,
            city: l.city,
            county: l.county,
            province: l.province,
            postalCode: l.postal_code,
            listPrice: l.list_price,
            neighborhood: l.neighborhood,
            status: l.status,
            type: l.type,
            dom: l.dom,
            availability: l.availability,
            expiry: l.expiry,
          };
          return parsed;
        });

        setState({
          listings: parsedListings,
          loading: false,
          creator,
          searchParams,
          agentName,
          branchName,
          subBrokerageName,
        });
      } catch (err) {
        errorReporter.error('error fetching brokerage pdf data');
      }
    };

    fetchData();
  }, []);

  const {
    creator,
    listings,
    loading,
    searchParams,
    agentName,
    branchName,
    subBrokerageName,
  } = state;

  const { getLabel, getAvailability } = useContext(ListingLabelContext);

  const dateOfReport = moment().format('MMM Do, YYYY');
  // Loading spinner
  if (loading) {
    return <Loading size={'fa-3x'} />;
  }

  const AvailabilityLabel = ({
    availability,
  }: {
    availability: ListingAvailability;
  }) => (
      <div>{`${getLabel('Availability')}: ${getAvailability(
        availability,
      )}`}</div>
    );

  const StatusLabel = ({ status }: { status: ListingStatus }) => <div>{`${getLabel('Status')}: ${getLabel(status)}`}</div>;

  return (
    <>
      <div
        className="hidden-print"
        style={{
          minWidth: '8.5in',
          maxWidth: '8.5in',
          marginTop: '0.5in',
          margin: '10px auto',
          display: 'flex',
          flexFlow: 'row nowrap',
        }}
      >
        <button
          className="btn btn-success"
          onClick={(window as any).print}
          style={{ marginRight: '3em' }}
        >
          Print PDF
        </button>
      </div>

      <div className="hpanel">
        <div
          className="panel-body print-page"
          style={{
            minWidth: '8.5in',
            maxWidth: '8.5in',
            paddingLeft: '0.5in',
            paddingRight: '0.5in',
            margin: 'auto',
          }}
        >
          <h3 style={{ marginTop: '0', fontWeight: 'bold' }}>
            {' '}
            My Brokerage Listings
          </h3>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <h5>Search Filter</h5>
              {searchParams.minPrice && (
                <div>
                  Min Price:{' '}
                  {parseInt(searchParams.minPrice, 10).toLocaleString()}
                </div>
              )}
              {searchParams.maxPrice && (
                <div>
                  Max Price:{' '}
                  {parseInt(searchParams.maxPrice, 10).toLocaleString()}
                </div>
              )}
              {searchParams.status && (
                <StatusLabel status={searchParams.status as ListingStatus} />
              )}
              {branchName && <div>Branch: {branchName}</div>}
              {subBrokerageName && <div>Sub Brokerage: {subBrokerageName}</div>}
              {searchParams.avail && (
                <AvailabilityLabel
                  availability={searchParams.avail as ListingAvailability}
                />
              )}
              {agentName && <div>Agent: {agentName}</div>}
            </div>

            <div>
              <div>
                <div>Created By:</div>
                <div>{creator}</div>
              </div>
              <div>
                <div>Date of Report:</div>
                <div>{dateOfReport}</div>
              </div>
            </div>
          </div>
          <div>
            <div>
              <ListingComponent listings={listings} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const BrokeragePDFComponent: React.FC<BrokeragePDFProps> = props => {
  const onboardingPortalId = useMemo(() => {
    const {
      $window: {
        location: { search },
      },
    } = props;
    const searchParams = queryString.parse(search);

    return searchParams.onboardingPortalId as string | undefined;
  }, [props.$window]);

  return (
    <ListingLabelContextProvider onboardingPortalId={onboardingPortalId}>
      <InnerBrokeragePDFComponent {...props} />
    </ListingLabelContextProvider>
  );
};

export default BrokeragePDFComponent;
